/**
 * ============================================================
 * KEJJA OS — src/hooks/useRoutines.ts
 * ============================================================
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { routinesApi, type RoutineCadence, type RoutineSchedule } from '@/lib/routinesApi';

// ============================================================
// QUERIES
// ============================================================

export function useRoutinesPulse(homeId: string | undefined) {
  return useQuery({
    queryKey: ['routines', homeId, 'pulse'],
    queryFn: () => routinesApi.pulse(homeId!),
    enabled: Boolean(homeId),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useRoutinesToday(homeId: string | undefined) {
  return useQuery({
    queryKey: ['routines', homeId, 'today'],
    queryFn: () => routinesApi.today(homeId!),
    enabled: Boolean(homeId),
    staleTime: 15 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useRoutines(homeId: string | undefined) {
  return useQuery({
    queryKey: ['routines', homeId, 'list'],
    queryFn: () => routinesApi.list(homeId!),
    enabled: Boolean(homeId),
  });
}

export function useRoutineCheckins(
  homeId: string | undefined, routineId: string | undefined, since?: string,
) {
  return useQuery({
    queryKey: ['routines', homeId, 'checkins', routineId, since],
    queryFn: () => routinesApi.listCheckins(homeId!, routineId!, since),
    enabled: Boolean(homeId) && Boolean(routineId),
  });
}

// ============================================================
// MUTATIONS
// ============================================================

function invalidateRoutines(qc: ReturnType<typeof useQueryClient>, homeId: string) {
  qc.invalidateQueries({ queryKey: ['routines', homeId] });
}

export function useCreateRoutine(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      description?: string | null;
      userId?: string | null;
      cadence: RoutineCadence;
      scheduleJson?: RoutineSchedule;
      timeOfDay?: string | null;
      icon?: string | null;
      color?: string | null;
    }) => routinesApi.create(homeId!, body),
    onSuccess: () => { if (homeId) invalidateRoutines(qc, homeId); },
  });
}

export function useUpdateRoutine(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ routineId, patch }: { routineId: string; patch: Parameters<typeof routinesApi.update>[2] }) =>
      routinesApi.update(homeId!, routineId, patch),
    onSuccess: () => { if (homeId) invalidateRoutines(qc, homeId); },
  });
}

export function useDeleteRoutine(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (routineId: string) => routinesApi.remove(homeId!, routineId),
    onSuccess: () => { if (homeId) invalidateRoutines(qc, homeId); },
  });
}

export function useCheckoffRoutine(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ routineId, date, note }: { routineId: string; date?: string; note?: string }) =>
      routinesApi.checkoff(homeId!, routineId, date, note),
    onSuccess: () => { if (homeId) invalidateRoutines(qc, homeId); },
  });
}

export function useUncheckRoutine(homeId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ routineId, date }: { routineId: string; date?: string }) =>
      routinesApi.uncheck(homeId!, routineId, date),
    onSuccess: () => { if (homeId) invalidateRoutines(qc, homeId); },
  });
}
